"use client";

import { useCallback, useMemo } from "react";
import {
  createDesignSpecSnapshot,
  diffDesignSpecFields,
  getActiveRenderedDesignSpec,
} from "@/lib/agent/designSpecSnapshot";
import { compileCurrentDesignSpec } from "@/services/turn/brief/briefForm";
import { useCreatePageStore } from "@/stores/createPageStore";
import type { DesignSpecFieldChange } from "@/types/generatedImage";
import {
  buildDesignPreviewUpdatePrompt,
  projectPreviewFreshness,
  projectSavedBackStaleness,
  getSideRelevantDesignChanges,
  type DesignPreviewSyncKind,
} from "@/lib/designer/previewFreshness";
import {
  getBackDesignSpecForLatestFront,
  getGeneratedDesignSpec,
  getGeneratedFrontDesignSpec,
} from "@/lib/designer/savedDesign";
import { useChatActions } from "./ChatActionsProvider";

export interface DesignPreviewSyncState {
  kind: DesignPreviewSyncKind | null;
  changes: DesignSpecFieldChange[];
  updatePreview: () => void;
  /** Refresh Back against the saved Front without applying uncommitted edits. */
  updateBackFromSavedFront: () => void;
  savedBackStale: boolean;
  updating: boolean;
  updateDisabled: boolean;
}

/**
 * Compares the editable draft with the immutable contract on one rendered
 * image. Updating is always an explicit customer action and submits only the
 * changed-field instruction alongside the complete latest draft contract.
 */
export function useDesignPreviewSync(
  side: "front" | "back"
): DesignPreviewSyncState {
  const { modifyImage, generateImages } = useChatActions();
  const formData = useCreatePageStore(state => state.form.data);
  const designBrief = useCreatePageStore(state => state.agent.designBrief);
  const renderedImages = useCreatePageStore(state => state.generation.images);
  const generationLoading = useCreatePageStore(
    state => state.generation.progress.loading
  );
  const isModifying = useCreatePageStore(
    state => state.ui.modification.isModifying
  );
  const generationPhase = useCreatePageStore(
    state => state.generation.progress.phase
  );
  const agentRequestActive = useCreatePageStore(
    state =>
      state.agent.conversation.isTyping ||
      state.agent.conversation.presetsLoading ||
      Boolean(state.agent.activeRequestController)
  );

  const activeImage = renderedImages[side];
  const activeImageUrl = activeImage.url?.trim() || null;
  const activeImagePath = activeImage.path?.trim() || null;
  const draftDesignSpec = useMemo(
    () =>
      compileCurrentDesignSpec(designBrief, formData, {
        hasFrontImage: Boolean(renderedImages.front.url),
        hasBackImage: Boolean(renderedImages.back.url),
      }),
    [designBrief, formData, renderedImages.back.url, renderedImages.front.url]
  );
  const renderedDesignSpec = getActiveRenderedDesignSpec(renderedImages, side);
  const { changes, freshness } = useMemo(
    () =>
      projectPreviewFreshness({
        side,
        hasImage: Boolean(activeImageUrl),
        renderedSpec: renderedDesignSpec,
        draftSpec: draftDesignSpec,
      }),
    [side, activeImageUrl, renderedDesignSpec, draftDesignSpec]
  );
  const savedBackStale = useMemo(
    () => side === "back" && projectSavedBackStaleness(renderedImages).stale,
    [renderedImages, side]
  );
  const kind: DesignPreviewSyncKind | null =
    freshness === "empty"
      ? null
      : freshness === "changes_pending" || savedBackStale
        ? "dirty"
        : freshness === "current"
          ? "clean"
          : "unknown";
  const hasUnappliedDraftChanges = useMemo(() => {
    if (!savedBackStale || !draftDesignSpec) return false;
    const savedSpec = getGeneratedDesignSpec(renderedImages);
    if (savedSpec)
      return diffDesignSpecFields(savedSpec, draftDesignSpec).length > 0;
    // Legacy Back images have no reliable contract. Their editable Back
    // description is the best available direction, but a pending Front edit
    // still needs its own render before this one-credit Back refresh.
    const savedFront = getGeneratedFrontDesignSpec(renderedImages.front);
    return Boolean(
      savedFront &&
      getSideRelevantDesignChanges(
        diffDesignSpecFields(savedFront, draftDesignSpec),
        "front"
      ).length
    );
  }, [draftDesignSpec, renderedImages, savedBackStale]);
  const updateDisabled =
    generationLoading ||
    isModifying ||
    agentRequestActive ||
    !activeImagePath ||
    (savedBackStale &&
      (!renderedImages.front.path?.trim() ||
        !getGeneratedFrontDesignSpec(renderedImages.front))) ||
    hasUnappliedDraftChanges;

  const updateBackFromSavedFront = useCallback(() => {
    if (side !== "back") return;
    const currentState = useCreatePageStore.getState();
    if (
      currentState.generation.progress.loading ||
      currentState.ui.modification.isModifying ||
      currentState.agent.conversation.isTyping ||
      currentState.agent.conversation.presetsLoading ||
      currentState.agent.activeRequestController
    )
      return;
    const images = currentState.generation.images;
    if (!images.back.url?.trim() || !images.back.path?.trim()) return;
    if (!images.front.path?.trim()) return;
    const savedFront = getGeneratedFrontDesignSpec(images.front);
    if (!savedFront) return;
    const savedSpec = getGeneratedDesignSpec(images);
    const savedBack = projectSavedBackStaleness(images);
    if (!savedBack.stale) return;
    const currentDraft = compileCurrentDesignSpec(
      currentState.agent.designBrief,
      currentState.form.data,
      { hasFrontImage: true, hasBackImage: true }
    );
    if (!currentDraft) return;
    if (
      savedSpec
        ? diffDesignSpecFields(savedSpec, currentDraft).length
        : getSideRelevantDesignChanges(
            diffDesignSpecFields(savedFront, currentDraft),
            "front"
          ).length
    )
      return;
    const renderSpec =
      savedSpec ?? getBackDesignSpecForLatestFront(images, currentDraft);
    if (!renderSpec) return;

    currentState.setCurrentView("back");
    // Generate from the latest saved Front, not from the obsolete Back image.
    // Legacy sessions without a Back contract use their editable Back choice.
    void generateImages(4, true, renderSpec, {
      side: "back",
      regenerateBackFromFront: true,
      suppressAgentReview: true,
    });
  }, [generateImages, side]);

  const updatePreview = useCallback(() => {
    // Re-read at click time so a just-saved field edit cannot race this action.
    const currentState = useCreatePageStore.getState();
    if (
      currentState.generation.progress.loading ||
      currentState.ui.modification.isModifying ||
      currentState.agent.conversation.isTyping ||
      currentState.agent.conversation.presetsLoading ||
      currentState.agent.activeRequestController
    ) {
      return;
    }
    const currentImage = currentState.generation.images[side];
    if (!currentImage.url?.trim() || !currentImage.path?.trim()) return;

    const currentDraft = createDesignSpecSnapshot(
      compileCurrentDesignSpec(
        currentState.agent.designBrief,
        currentState.form.data,
        {
          hasFrontImage: Boolean(currentState.generation.images.front.url),
          hasBackImage: Boolean(currentState.generation.images.back.url),
        }
      )
    );
    if (
      side === "back" &&
      projectSavedBackStaleness(currentState.generation.images).stale
    ) {
      updateBackFromSavedFront();
      return;
    }

    const currentRendered = getActiveRenderedDesignSpec(
      currentState.generation.images,
      side
    );
    if (!currentDraft || !currentRendered) return;

    const currentChanges = diffDesignSpecFields(currentRendered, currentDraft);
    if (getSideRelevantDesignChanges(currentChanges, side).length === 0) return;

    currentState.setCurrentView(side);
    void modifyImage(
      buildDesignPreviewUpdatePrompt(side, currentChanges),
      currentDraft
    );
  }, [modifyImage, side, updateBackFromSavedFront]);

  return {
    kind,
    changes,
    updatePreview,
    updateBackFromSavedFront,
    savedBackStale,
    updating: isModifying && generationPhase === side,
    updateDisabled,
  };
}
